import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { ProfessorDetail, SessionResponse } from "../../shared/types";
import { termSortValue } from "../../shared/terms";
import { LoginWallCard } from "../components/LoginWallCard";
import { StaticDataState } from "../components/StaticDataState";
import { fetchProfessor, StaticDataNotFoundError } from "../lib/static-data";

export function ProfessorPage(props: { session: SessionResponse["user"] }) {
  const { slug = "" } = useParams();
  const [professor, setProfessor] = useState<ProfessorDetail | null>(null);
  const [isMissing, setIsMissing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [expandedCourseIds, setExpandedCourseIds] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!slug) {
      return;
    }

    setProfessor(null);
    setIsMissing(false);
    setLoadError(null);
    setExpandedCourseIds({});
    void fetchProfessor(slug)
      .then((nextProfessor) => {
        setProfessor(nextProfessor);
        setIsMissing(false);
      })
      .catch((error) => {
        setProfessor(null);
        if (error instanceof StaticDataNotFoundError) {
          setIsMissing(true);
          return;
        }
        setLoadError("Could not load this professor page right now.");
      });
  }, [reloadKey, slug]);

  if (loadError) {
    return <StaticDataState title="Professor page could not load." message={loadError} error onRetry={() => setReloadKey((value) => value + 1)} />;
  }

  if (!professor && !isMissing) {
    return <section className="panel">Loading professor details...</section>;
  }

  if (!professor) {
    return (
      <section className="panel stack-md">
        <div className="section-heading">
          <p className="eyebrow">Professor</p>
          <h1>That professor page is not in the catalog.</h1>
        </div>
        <Link to="/professors/" className="primary-link">
          Back to professor search
        </Link>
      </section>
    );
  }

  const reviewedCourses = [...professor.courses]
    .filter((course) => course.reviews.length > 0)
    .sort((left, right) => {
      const reviewTermDelta = termSortValue(right.reviews[0]?.term ?? "") - termSortValue(left.reviews[0]?.term ?? "");
      if (reviewTermDelta !== 0) {
        return reviewTermDelta;
      }
      if (right.reviewCount !== left.reviewCount) {
        return right.reviewCount - left.reviewCount;
      }
      return left.shortName.localeCompare(right.shortName);
    });
  const reviewPreviewWallActive = !props.session;

  function toggleExpandedCourse(courseId: string) {
    setExpandedCourseIds((current) => ({
      ...current,
      [courseId]: !current[courseId],
    }));
  }

  return (
    <div className="stack-xl">
      <section className="panel stack-lg">
        <div className="section-heading">
          <p className="eyebrow">Professor Reviews</p>
          <h1>{professor.name}</h1>
        </div>
        <p className="lede">
          Read student reviews grouped by course, with the newest quarters first.
        </p>
        <div className="feature-grid">
          <article className="department-summary">
            <span>Reviews</span>
            <strong>{professor.reviewCount}</strong>
            <small>Total student reviews in the archive</small>
          </article>
          <article className="department-summary">
            <span>Courses</span>
            <strong>{professor.courseCount}</strong>
            <small>Courses in the archive</small>
          </article>
          <article className="department-summary">
            <span>Most recent offering</span>
            <strong>{professor.mostRecentTerm ?? "Unknown"}</strong>
            <small>{professor.departments.join(", ")}</small>
          </article>
        </div>
      </section>

      <section className="panel stack-lg">
        <div className="section-heading">
          <p className="eyebrow">Courses</p>
          <h2>Most recent offering by this professor</h2>
        </div>
        <div className="results-grid">
          {professor.courses.map((course) => (
            <Link key={course.courseId} to={`/course/${course.slug}/`} className="course-link-card">
              <strong>{course.shortName}</strong>
              <span>{course.title}</span>
              <small>{course.mostRecentTerm ?? "Unknown"}</small>
            </Link>
          ))}
        </div>
        <div className="section-heading">
          <p className="eyebrow">Reviews</p>
          <h2>Course reviews</h2>
        </div>
        <div className="stack-lg">
          {reviewedCourses.map((course) => (
            <article key={course.courseId} className="panel stack-md selected-course-panel">
              <div className="course-group-heading">
                <div className="stack-md">
                  <Link to={`/course/${course.slug}/`} className="course-link-card">
                    <strong>{course.shortName}</strong>
                    <span>{course.title}</span>
                  </Link>
                  <p className="toolbar-summary">
                    {course.reviewCount} review{course.reviewCount === 1 ? "" : "s"}
                  </p>
                </div>
                <small>{course.departmentName || course.department}</small>
              </div>
              <div className="review-list">
                {course.reviews.slice(0, 3).map((review) => (
                  <article key={review.id} className="review-card">
                    <div className="review-meta">
                      <strong>{review.term}</strong>
                      <span>{professor.name}</span>
                      {review.median ? <span>Median {review.median}</span> : null}
                    </div>
                    <p>{review.comments}</p>
                  </article>
                ))}
                {reviewPreviewWallActive && course.reviews.length > 3 ? (
                  <article className="review-preview-wall">
                    <div className="blurred-review-stack" aria-hidden="true">
                      {course.reviews.slice(3, 5).map((review) => (
                        <article key={review.id} className="review-card">
                          <div className="review-meta">
                            <strong>{review.term}</strong>
                            <span>{professor.name}</span>
                            {review.median ? <span>Median {review.median}</span> : null}
                          </div>
                          <p>{review.comments}</p>
                        </article>
                      ))}
                    </div>
                    <div className="review-preview-overlay">
                      <LoginWallCard
                        compact
                        title="Sign up or log in to view more course reviews"
                        body="Create an account or log in with your Dartmouth email to unlock all the reviews for this course."
                      />
                    </div>
                  </article>
                ) : null}
                {!reviewPreviewWallActive && course.reviews.length > 3 ? (
                  <>
                    <div
                      className="expandable-review-block"
                      data-expanded={expandedCourseIds[course.courseId] ? "true" : "false"}
                    >
                      <div className="expandable-review-inner">
                        <div className="review-list">
                          {course.reviews.slice(3).map((review) => (
                            <article key={review.id} className="review-card">
                              <div className="review-meta">
                                <strong>{review.term}</strong>
                                <span>{professor.name}</span>
                                {review.median ? <span>Median {review.median}</span> : null}
                              </div>
                              <p>{review.comments}</p>
                            </article>
                          ))}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="ghost-button review-toggle-button"
                      onClick={() => toggleExpandedCourse(course.courseId)}
                    >
                      {expandedCourseIds[course.courseId] ? "Show fewer reviews" : "Show more reviews"}
                    </button>
                  </>
                ) : null}
              </div>
            </article>
          ))}
          {reviewedCourses.length === 0 ? (
            <p className="notice-banner">No imported student reviews are available for this professor yet.</p>
          ) : null}
        </div>
      </section>
    </div>
  );
}
